import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { filter, map } from 'lodash'
import Button from '../Button'
import { wpAxios } from '../../config/axiosResolver'
import { sendReferrals } from './sendReferrals'

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`

const ModalHeader = styled.div`
  margin-bottom: 20px;
`

const ModalTitle = styled.h2`
  font-family: Hind;
  font-size: 24px;
  color: #000;
  margin: 0;
  font-weight: 400;
`

const ModalSubtitle = styled.p`
  font-family: Hind;
  font-size: 16px;
  color: #666;
  margin: 10px 0 0 0;
  font-weight: 300;
`

const IncentiveText = styled.div`
  background: #f8f9fa;
  border: 2px solid #ECB595;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
  text-align: center;
`

const IncentiveDescription = styled.div`
  font-family: Hind;
  font-size: 14px;
  color: #333;
  font-weight: 400;
  line-height: 1.4;
`

const VendorsList = styled.div`
  flex: 1;
  overflow-y: auto;
  margin: 20px 0;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 15px;
`

const VendorItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`

const VendorCheckbox = styled.input`
  margin-right: 15px;
  width: 18px;
  height: 18px;
  cursor: pointer;
`

const VendorInfo = styled.div`
  flex: 1;
`

const VendorName = styled.div`
  font-family: Hind;
  font-size: 16px;
  color: #000;
  font-weight: 400;
`

const VendorType = styled.div`
  font-family: Hind;
  font-size: 14px;
  color: #666;
  font-weight: 300;
`

const VendorEmail = styled.div`
  font-family: Hind;
  font-size: 14px;
  color: #999;
  font-weight: 300;
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`

const VendorCount = styled.div`
  font-family: Hind;
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`

const SendError = styled.div`
  font-family: Hind;
  font-size: 14px;
  color: #b00020;
  margin: 10px 0 0;
`

const VendorReferralModal = ({ isOpen, onClose, credits, onSendReferrals, submissionId, submission, submitterVendorId, vendorName }) => {
  const [selectedVendors, setSelectedVendors] = useState([])
  const [eligibleVendors, setEligibleVendors] = useState([])
  const [isSubmitting , setIsSubmitting] = useState(false)
  const [sendError, setSendError] = useState(null)

  useEffect(() => {
    if (isOpen && credits) {
      // Filter for vendors/venues that are not LBB and have email addresses
      const nonLbbVendorsWithEmail = filter(credits, (credit) => {
        return !credit.isLbb && credit.email && credit.email.trim() !== ''
      })
      
      // Deduplicate vendors by email address (same vendor may be credited multiple times)
      const uniqueVendors = []
      const seenEmails = new Set()
      
      nonLbbVendorsWithEmail.forEach((vendor) => {
        if (!seenEmails.has(vendor.email)) {
          seenEmails.add(vendor.email)
          // Combine all service types for this vendor
          const allServices = nonLbbVendorsWithEmail
            .filter(v => v.email === vendor.email)
            .map(v => v.type)
            .join(', ')
          
          uniqueVendors.push({
            ...vendor,
            type: allServices // Show all services this vendor provides
          })
        }
      })
      
      setEligibleVendors(uniqueVendors)
      // Initially select all unique vendors
      setSelectedVendors(map(uniqueVendors, 'id'))
    }
  }, [isOpen, credits])

  const handleToggleVendor = (vendorId) => {
    setSelectedVendors(prev => {
      if (prev.includes(vendorId)) {
        return prev.filter(id => id !== vendorId)
      } else {
        return [...prev, vendorId]
      }
    })
  }

  const handleSendReferrals = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    
    const vendorsToSend = filter(eligibleVendors, vendor => 
      selectedVendors.includes(vendor.id)
    )
    
    await sendReferrals({
      post: () => wpAxios.post('https://www.stylemepretty.com/api/v2/submissions/send-referral', {
        vendors: vendorsToSend.map(vendor => ({
          vendor_id: vendor.id,
          vendor_name: vendor.name,
          vendor_email: vendor.email
        })),
        submission_id: submissionId,
        referrer_email: submission.submit_email,
        referrer_name: vendorName || 'A fellow vendor',
        referrer_vendor_id: submitterVendorId
      }),
      onSent: () => onSendReferrals(vendorsToSend),
      onClose,
      setError: setSendError,
      setSubmitting: setIsSubmitting
    })
  }

  const handleSkip = () => {
    onClose()
  }

  if (!isOpen) return null

  return (
    <ModalOverlay onClick={handleSkip}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Send LBB Referrals</ModalTitle>
          <ModalSubtitle>
            We noticed some vendors in your submission aren't Little Black Book members yet. 
            Send them an invitation and earn rewards!
          </ModalSubtitle>
          <IncentiveText>
            <IncentiveDescription>
              For each vendor that signs up for Little Black Book membership through your referral, 
              you'll receive $100 off your next LBB renewal or perk purchase.
            </IncentiveDescription>
          </IncentiveText>
        </ModalHeader>
        
        {eligibleVendors.length > 0 ? (
          <>
            <VendorCount>
              {selectedVendors.length} of {eligibleVendors.length} vendors selected
            </VendorCount>
            <VendorsList>
              {map(eligibleVendors, (vendor) => (
                <VendorItem key={vendor.id}>
                  <VendorCheckbox
                    type="checkbox"
                    checked={selectedVendors.includes(vendor.id)}
                    onChange={() => handleToggleVendor(vendor.id)}
                  />
                  <VendorInfo>
                    <VendorName>{vendor.name}</VendorName>
                    <VendorType>{vendor.type}</VendorType>
                    <VendorEmail>{vendor.email}</VendorEmail>
                  </VendorInfo>
                </VendorItem>
              ))}
            </VendorsList>
            {sendError && <SendError>{sendError}</SendError>}
            <ModalFooter>
              <Button 
                text="Skip" 
                onClick={handleSkip}
                style={{ 
                  backgroundColor: 'transparent', 
                  color: '#666',
                  border: '1px solid #ddd' 
                }}
              />
              <Button 
                text={`Send Referrals (${selectedVendors.length})`}
                onClick={handleSendReferrals}
                disabled={selectedVendors.length === 0 || isSubmitting}
              />
            </ModalFooter>
          </>
        ) : (
          <>
            <VendorsList>
              <p style={{ textAlign: 'center', color: '#999', fontFamily: 'Hind' }}>
                No eligible vendors found for referrals. All vendors are either already LBB members or don't have email addresses.
              </p>
            </VendorsList>
            <ModalFooter>
              <Button text="Close" onClick={handleSkip} />
            </ModalFooter>
          </>
        )}
      </ModalContainer>
    </ModalOverlay>
  )
}

export default VendorReferralModal